import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useState } from "react";
import DataBox from "../../components/data-box/DataBox.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import SectionTitle from "../../components/page-item/SectionTitle.js";
import { useUser } from "../../components/UserContext.js";
import { getCharacter, getHost } from "../../lib/apiConfig.js";
import { PAGE_CONTAINER_SX, PAGE_TITLE_SX } from "../../lib/constants.js";
import { useFetch } from "../../lib/hooks/useFetch.js";
import PrimarySecondaryList from "./components/PrimarySecondaryList.js";

export default function HostBackground({ locales }) {
  const { user, runId } = useUser();
  const [host, setHost] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingHost } = useFetch({
    endpoint: getHost({ id: user.host.id, runId }),
    mustFetch: !host,
    onSuccess: (response) => {
      setHost(response);
      setCharacterId(response?.character?.id);
    },
    onFail: setError,
  });

  const { isFetching: isFetchingCharacter } = useFetch({
    endpoint: getCharacter({ id: characterId, runId }),
    mustFetch: characterId && !character,
    onSuccess: setCharacter,
    onFail: setError,
  });

  return !host || isFetchingHost || isFetchingCharacter ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Typography id="background-header" component="h1" sx={PAGE_TITLE_SX}>
        {character?.name}
      </Typography>
      <Grid container id="background-page">
        <Grid xs={12} smd={4} md={3}>
          <DataBox locales={locales} character={character} host={host} />
        </Grid>
        <Grid xs={12} smd={1} />
        <Grid xs={12} smd={7} md={8} sx={{ mt: { xs: 4, smd: 0 } }}>
          <Box display="flex" flexDirection="column" gap={4}>
            <Typography
              id="background-story"
              sx={{ whiteSpace: "pre-wrap" }}
              variant="body1"
            >
              {character?.story}
            </Typography>
            {character?.personality ? (
              <Box id="background-personality">
                <SectionTitle text={locales["character.personality"]} />
                <Typography sx={{ whiteSpace: "pre-wrap" }} variant="body1">
                  {character?.personality}
                </Typography>
              </Box>
            ) : null}
            {character?.involved?.length ? (
              <Box id="background-plots">
                <SectionTitle text={locales["character.plots"]} />
                <PrimarySecondaryList
                  array={character.involved}
                  primaryId="plot-name"
                  primaryProp="plot.name"
                  secondaryId="plot-involvement"
                  secondaryProp="involvement"
                />
              </Box>
            ) : null}
            {character?.relationships?.length ? (
              <Box id="background-relations">
                <SectionTitle text={locales["character.relations"]} />
                <PrimarySecondaryList
                  array={character.relationships}
                  hasAvatar
                  primaryId="background-relation-name"
                  primaryProp="object.name"
                  secondaryId="background-relation-description"
                  secondaryProp="description"
                />
              </Box>
            ) : null}
          </Box>
        </Grid>
      </Grid>
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
